/**
 * Created by rathompson on 11/5/21
 * File: AssignRolesForm
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

// Libs
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  Button,
  LoadingIcon,
  Select,
  useModal,
} from '@panwds/react-ui'
import {
  Form,
  FormLayout,
} from '@panwds/react-form'
import { PlusIcon } from '@panwds/icons'
import _ from 'lodash'

// Context
import AppContext from '../../../../common/context/AppContext'
import GlobalContext from '../../../../common/context/GlobalContext'

// Components
import Assignment from './Assignment'
import ViewRolesModal from '../../roles/modal/ViewRolesModal/ViewRolesModal'

// Utils
import { ARRAY, OBJECT } from '../../../../common/utils/Utils'
import { getVarValue } from '../../../../common/utils/Sparky'
import { cloneDeep } from 'lodash'

// Constants
import {
  FF_ALLOW_PA_ROLE,
  FF_ALLOW_CDL_ROLE,
  FF_ALLOW_CIE_ROLE,
  FF_ALLOW_SDWAN_ROLE,
  FF_ALLOW_IOT_ROLE,
  FF_USE_APPLICABLE_ROLES,
  FF_ALLOW_DLP_ROLE,
  FF_ALLOW_NG_CASB_ROLE,
  FF_HIDE_MSP_ROLES,
  FF_ALLOW_STRATA_INSIGHTS_ROLE,
  FF_CIE_VIEW_ONLY
} from '../../../../common/utils/FeatureFlags'
import {
  APP_ID_PRISMA_ACCESS,
  APP_ID_SDWAN,
  APP_ID_LOGGING_SERVICE,
  APP_ID_DSS_ID,
  APP_ID_IOT,
  APP_ID_DLP,
  APP_ID_NG_CASB,
  APP_ID_STRATA_INSIGHTS,
  APP_ID_STRATA_INSIGHTS_FREE,
} from '../../../../common/constants/GlobalConstants'
import {
  BUTTON_ADD_ANOTHER,
  LABEL_ALL_APPS_SERVICES,
  LABEL_APPS_SERVICES,
  LABEL_ROlE,
  LABEL_ALREADY_SUPERUSER,
  LABEL_HAS_SUPER_FOR_ALL
} from '../../../constants/LabelConstants'
import { Bold } from '@panwds/react-ui'
import { StringCompare } from '../../../utils/string'
import IAMAppName from '../../../utils/IAMAppName'
import useReadOnly from '../../../../common/hooks/useReadOnly'
import { IDENTITIES_PERMISSION_SET } from '../../../constants/IAMConstants'
import TooltipButton from '../../../../common/components/tooltipButton/TooltipButton'

const allowPARole = getVarValue(FF_ALLOW_PA_ROLE, true)
const allowCDLRole = getVarValue(FF_ALLOW_CDL_ROLE, true)
const allowCIERole = getVarValue(FF_ALLOW_CIE_ROLE, true)
const allowDLPRole = getVarValue(FF_ALLOW_DLP_ROLE, false)
const allowSDWANRole = getVarValue(FF_ALLOW_SDWAN_ROLE, false)
const allowIOTRole = getVarValue(FF_ALLOW_IOT_ROLE, false)
const allowCASBRole = getVarValue(FF_ALLOW_NG_CASB_ROLE, false)
const allowAIOPsRole = getVarValue(FF_ALLOW_STRATA_INSIGHTS_ROLE, false)
const useApplicableRoles = getVarValue(FF_USE_APPLICABLE_ROLES, false)

const hideMSPRoles = getVarValue(FF_HIDE_MSP_ROLES, false)
const cieViewOnly = getVarValue(FF_CIE_VIEW_ONLY, false)

const ID_PREDEFINED = 'predefined-header'
const ID_CUSTOM = 'custom-header'
const ID_SUPERUSER = 'superuser-header'

/**
 *   Reusable role selection form
 */
const AssignRolesForm = ({ children, tenant, isLoading, onSubmit: _onSubmit, member, optional }) => {
  /**
   *     ========= Local State ===========
   */
  const [apps, setApps] = useState([])
  const { iamReducer = {}, treeReducer = {} } = useContext(GlobalContext)
  const { selectedTreeNode = {} } = treeReducer
  const { getAppName } = useContext(AppContext)
  const { roles: _roles = [], customRoles = [] } = iamReducer
  const readOnly = useReadOnly(selectedTreeNode?.id, IDENTITIES_PERMISSION_SET)
  const roleModal = useModal(ViewRolesModal)

  const supportedApps = useMemo(() => [
    'all',
    APP_ID_PRISMA_ACCESS,
    APP_ID_SDWAN,
    APP_ID_LOGGING_SERVICE,
    APP_ID_DSS_ID,
    APP_ID_IOT,
    APP_ID_DLP,
    APP_ID_NG_CASB,
    APP_ID_STRATA_INSIGHTS,
    APP_ID_STRATA_INSIGHTS_FREE
  ], [])

  const hideMspRoles = useMemo(() => hideMSPRoles && tenant?.isAbsoluteRoot && !ARRAY.hasArray(tenant?.children), [tenant])

  const roles = useMemo(() => {
    if (useApplicableRoles) {
      const roles = []
      roles.push({
        id: ID_PREDEFINED,
        disabled: true,
        children: (
          <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-pl-3'>
            <Bold appearance='semibold' addClassName='tw-text-secondary'>PREDEFINED ROLES</Bold>
          </div>
        ),
        apps: supportedApps
      })
      const predefinedRoles = []
      for (const _role of _roles) {
        const role = cloneDeep(_role)
        if (`${role?.name}`.toLowerCase().startsWith('msp') && hideMspRoles) {
          if (member?.policies?.find(policy => policy?.role === role?.name)) {
            role.disabled = true
          }
          else {
            continue
          }
        }
        // Check if role is not common
        if (role.app_id && role.name !== 'adem_tier_1_support') {
          role.apps = [role.app_id] // Role can only be assigned to one app
        }
        else {
          // Role is common
          switch (role.name) {
            case 'superuser':
              role.apps = supportedApps
              break
            case 'msp_superuser':
              role.apps = supportedApps.filter(app => ![APP_ID_STRATA_INSIGHTS_FREE, APP_ID_STRATA_INSIGHTS].includes(app))
              break
            case 'view_only_admin':
              role.apps = ['all', APP_ID_PRISMA_ACCESS, APP_ID_SDWAN, APP_ID_IOT, APP_ID_DLP, APP_ID_NG_CASB]
              if (cieViewOnly) {
                role.apps.push(APP_ID_DSS_ID)
              }
              break
            case 'data_security_admin':
              role.apps = ['all', APP_ID_PRISMA_ACCESS, APP_ID_NG_CASB]
              break
            case 'security_admin':
              role.apps = ['all', APP_ID_PRISMA_ACCESS, APP_ID_SDWAN, APP_ID_NG_CASB]
              break
            case 'auditor':
            case 'tier_2_support':
            case 'tier_1_support':
              role.apps = ['all', APP_ID_PRISMA_ACCESS]
              break
            case 'iam_admin':
            case 'msp_iam_admin':
            case 'network_admin':
              role.apps = ['all', APP_ID_PRISMA_ACCESS, APP_ID_SDWAN]
              break
            case 'soc_analyst':
              role.apps = ['all', APP_ID_PRISMA_ACCESS, APP_ID_LOGGING_SERVICE]
              break
            case 'deployment_admin':
              role.apps = ['all', APP_ID_DSS_ID]
              break
            case 'adem_tier_1_support':
              role.apps = [APP_ID_PRISMA_ACCESS, APP_ID_SDWAN]
              break
            case 'browser':
            case 'business_admin':
            default:
              role.apps = ['all']
          }
        }
        predefinedRoles.push(role)
      }
      predefinedRoles.sort((roleA, roleB) => StringCompare.Ascend(roleA.name, roleB.name))
      roles.push(...predefinedRoles)
      if (ARRAY.hasArray(customRoles)) {
        const _customRoles = []
        roles.push({
          id: ID_CUSTOM,
          disabled: true,
          children: (
            <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-pl-3'>
              <Bold appearance='semibold' addClassName='tw-text-secondary'>CUSTOM ROLES</Bold>
            </div>
          ),
          apps: supportedApps
        })
        for (const customRole of customRoles) {
          customRole.apps = supportedApps
          _customRoles.push(customRole)
        }
        _customRoles.sort((roleA, roleB) => StringCompare.Ascend(roleA.name, roleB.name))
        roles.push(..._customRoles)
      }
      return roles
    }
    return _roles
  }, [_roles, supportedApps, customRoles, hideMspRoles, member?.policies])

  const appItems = useMemo(() => {
    const items = []
    if (allowPARole) {
      items.push({ id: APP_ID_PRISMA_ACCESS, value: IAMAppName(APP_ID_PRISMA_ACCESS, getAppName) })
    }
    if (allowCDLRole) {
      items.push({ id: APP_ID_LOGGING_SERVICE, value: IAMAppName(APP_ID_LOGGING_SERVICE, getAppName) })
    }
    if (allowCIERole) {
      items.push({ id: APP_ID_DSS_ID, value: IAMAppName(APP_ID_DSS_ID, getAppName) })
    }
    if (allowIOTRole) {
      items.push({ id: APP_ID_IOT, value: IAMAppName(APP_ID_IOT, getAppName) })
    }
    if (allowSDWANRole) {
      items.push({ id: APP_ID_SDWAN, value: IAMAppName(APP_ID_SDWAN, getAppName) })
    }
    if (allowDLPRole) {
      items.push({ id: APP_ID_DLP, value: IAMAppName(APP_ID_DLP, getAppName) })
    }
    if (allowCASBRole) {
      items.push({ id: APP_ID_NG_CASB, value: IAMAppName(APP_ID_NG_CASB, getAppName) })
    }
    if (allowAIOPsRole) {
      items.push({ id: APP_ID_STRATA_INSIGHTS, value: IAMAppName(APP_ID_STRATA_INSIGHTS, getAppName) })
      items.push({ id: APP_ID_STRATA_INSIGHTS_FREE, value: IAMAppName(APP_ID_STRATA_INSIGHTS_FREE, getAppName) })
    }
    items.sort((itemA, itemB) => StringCompare.Ascend(`${itemA.value}`, `${itemB.value}`))
    items.unshift({ id: 'all', value: <Bold>{LABEL_ALL_APPS_SERVICES}</Bold> })
    return items
  }, [getAppName])

  const roleItems = useMemo(() => (roles ? roles?.map(role => ({ value: role?.label || role?.name, ...role })) : []), [roles])

  const roleMap = useMemo(() => apps.reduce((map, app) => {
    if (!map.hasOwnProperty(app?.id)) {
      map[app?.id] = []
    }
    if (ARRAY.hasArray(app?.roles)) {
      for (const role of app.roles) {
        if (role?.policy?.inherited_from === tenant?.id || !OBJECT.hasObject(role?.policy)) {
          map[app?.id].push(role?.name)
        }
      }
    }
    return map
  }, {}), [apps, tenant])

  const availableApps = useMemo(() => {
    return appItems.filter(({ id }) => !apps.filter(app => !app.disabled).find(app => app.id === id))
  }, [appItems, apps])

  useEffect(() => {

    const mapPolicies = (policies, disabled, isScoped = false) => {
      const policyMap = _.groupBy(policies, 'resource.app_id')
      return Object.entries(policyMap).map(([app_id, policies], index) => {
        const value = app_id ? IAMAppName(app_id, getAppName) : LABEL_ALL_APPS_SERVICES
        const roles = policies.map(policy => {
          const role = roleItems.find(({ id, name }) => policy.role === name || policy.role === id)
          let value = role?.value
          if (policy?.resource?.resource_scope) {
            value = `${value}/${policy.resource.resource_scope}`
          }
          return {
            value: value,
            name: role?.id || role?.name,
            policy,
            isScoped,
            display_name: role?.value
          }
        })
        const id = (app_id || 'all') + (disabled ? '-disabled' : '')
        return {
          id,
          value,
          index,
          name: value,
          disabled,
          roles,
          policies,
          isScoped
        }
      })
    }

    if (OBJECT.hasObject(member) && ARRAY.hasArray(member.policies)) {

      const scoped_policies = member.policies.filter(policy => !!policy.resource?.resource_scope)
      const inherited_policies = member.policies.filter(policy => !policy.resource?.resource_scope && policy.inherited_from !== selectedTreeNode?.id)
      const policies = member.policies.filter(policy => !policy.resource?.resource_scope && policy.inherited_from === selectedTreeNode?.id)

      const apps = mapPolicies(policies, false)
      const inherited_apps = mapPolicies(inherited_policies, true)
      const scoped_apps = mapPolicies(scoped_policies, true, true)

      setApps([...apps, ...inherited_apps, ...scoped_apps])
    }
  }, [member, roleItems, tenant?.id, getAppName, selectedTreeNode?.id])

  /**
   *     ========= Callback ===========
   */
  const onAppChange = useCallback((index, { id, value: name }) => {
    if (index >= 0) {
      apps[index] = { id, name, index }
    }
    else {
      apps.push({ id, name, index: apps.length })
    }
    setApps([...apps])
  }, [apps])

  const onRoleChange = useCallback(({ index }, roles) => {
    let updated_apps = [...apps]
    if (Number.isInteger(index) && index >= 0) {
      if (roles.some(role => ['superuser', 'msp_superuser'].includes(role.id))) {
        apps[index].roles = roles.filter(role => ['superuser', 'msp_superuser'].includes(role.id))
        if (apps[index].id === 'all') {
          updated_apps = updated_apps.filter(app => app.id === 'all')
        }
      }
      else {
        apps[index].roles = roles
      }
      setApps(updated_apps)
    }
  }, [apps])

  const onRemoveRole = useCallback((app) => {
    apps.splice(app.index, 1)
    const updated_apps = apps.map((app, index) => ({ ...app, index }))
    setApps(updated_apps)
  }, [apps])

  const getRoleItems = useCallback((app) => {
    const selectedRoles = roleMap[app?.id] || []
    if (selectedRoles.includes('superuser') || selectedRoles.includes('msp_superuser')) {
      return [
        {
          id: ID_SUPERUSER,
          disabled: true,
          children: (
            <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-pl-3'>
              <Bold appearance='semibold' addClassName='tw-text-secondary'>{LABEL_ALREADY_SUPERUSER}</Bold>
            </div>
          ),
          apps: supportedApps
        }
      ]
    }
    const items = roleItems.filter(({ apps }) => {
      return !useApplicableRoles || apps?.includes(app?.id)
    }).filter(({ name }) => !selectedRoles.includes(name)).map((item, index) => ({ key: index, ...item }))

    const allPredefinedSelected = !items.some(role => role.is_custom === false && ![ID_CUSTOM, ID_PREDEFINED, ID_SUPERUSER].includes(role.id))
    const allCustomSelected = !items.some(role => role.is_custom === true && ![ID_CUSTOM, ID_PREDEFINED, ID_SUPERUSER].includes(role.id))
    return items.filter(item => {
      if (allPredefinedSelected && !allCustomSelected) {
        return item.id !== ID_PREDEFINED
      }
      if (allCustomSelected && !allPredefinedSelected) {
        return item.id !== ID_CUSTOM
      }
      return !(allCustomSelected && allPredefinedSelected)
    })
  }, [roleItems, roleMap, supportedApps])

  const onRoleInfoClick = useCallback((app) => {
    roleModal.openModal({ app })
  }, [roleModal])

  const assignments = useMemo(() => {
    const data = ARRAY.hasArray(apps) ? apps : [{}]
    return data.map((app, index) => {
      return (<Assignment
        disabled={false}
        app={app}
        key={index}
        appItems={availableApps}
        roleItems={getRoleItems(app)}
        onAppChange={onAppChange}
        onRoleChange={onRoleChange}
        onRemove={onRemoveRole.bind(this, app)}
        optional={optional}
        onRoleInfoClick={onRoleInfoClick}
      />)
    })
  }, [apps, availableApps, getRoleItems, onAppChange, onRoleChange, onRemoveRole, optional, onRoleInfoClick])

  const addDisabled = useMemo(() => {
    if (!apps.length || readOnly) {
      return true
    }
    return !availableApps.length
  }, [apps, availableApps, readOnly])

  const hasSuperUserForAll = useMemo(() => {
    return roleMap?.['all']?.includes('superuser') || roleMap?.['all']?.includes('msp_superuser')
  }, [roleMap])

  const onSubmit = useCallback(() => {
    const body = apps.reduce((map, app) => {
      map[app.id] = app.roles
      return map
    }, {})
    _onSubmit(body)
  }, [_onSubmit, apps])

  return (
    <Form onSubmit={onSubmit} mode='onChange'>
      {isLoading ? (
        <div className='tw-flex tw-absolute tw-inset-0 tw-justify-center tw-items-center'>
          <LoadingIcon size="5xl"/>
        </div>
      ) : (
        <FormLayout>
          <div className='tw-flex tw-flex-row tw-gap-2'>
            <div style={{ flex: '0.5 0.5 0', width: 0 }}>
              <Bold appearance='semibold'>{LABEL_APPS_SERVICES}</Bold>
            </div>
            <div className='tw-pl-2' style={{ flex: '1 1 0', width: 0 }}>
              <Bold appearance='semibold' >{LABEL_ROlE}</Bold>
            </div>
          </div>
          {assignments}
          <div className='cm-m-0 cm-w-min'>
            {hasSuperUserForAll ? (
              <TooltipButton
                label={LABEL_HAS_SUPER_FOR_ALL}
                disabled
                buttonProps={{
                  isMenu: true,
                  appearance: 'secondary',
                  icon: <PlusIcon size='xs' />
                }}
              >{BUTTON_ADD_ANOTHER}</TooltipButton>
            ) : (
              <Select
                addClassName='cmm-modal-select'
                items={availableApps}
                fitToItemWidth
                disabled={addDisabled}
                onChange={({ selectedItem }) => {
                  onAppChange(undefined, selectedItem)
                }}
                button={
                  <Button
                    isMenu
                    appearance='secondary'
                    disabled={!readOnly}
                    icon={<PlusIcon size='xs' />}
                  >
                    {BUTTON_ADD_ANOTHER}
                  </Button>
                }
              />
            )}
          </div>
        </FormLayout>
      )}
      {children}
    </Form>
  )
}

AssignRolesForm.propTypes = {}
export default AssignRolesForm
